/**
 * Tropical Sun — componentes de marca
 * ---------------------------------------------------------------
 * Dois custom elements, ambos apoiados na infraestrutura nativa do
 * Horizon em vez de reimplementá-la:
 *
 *   <ts-pix-price>    reaproveita o HTML re-renderizado que o
 *                     variant-picker já busca, então o preço Pix
 *                     vem calculado pelo Liquid — sem duplicar a
 *                     math de dinheiro no cliente.
 *
 *   <ts-freeship-bar> escuta o evento padrão de carrinho e relê o
 *                     total via /cart.js.
 *
 * @module tropical-sun
 */

import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

/**
 * Preço Pix e parcelamento, sincronizado com a variante selecionada.
 *
 * Segue o mesmo padrão do <product-price> nativo: escuta o
 * productSelect na seção, espera a promise do evento e extrai do
 * HTML já re-renderizado o markup correspondente a este bloco.
 * Assim os valores continuam vindo do Liquid.
 */
class TsPixPrice extends Component {
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    if (!this.#section) return;
    this.#section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#section) return;
    this.#section.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    // Ignora trocas de variante disparadas dentro de um card de produto.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const incoming = html.querySelector(`ts-pix-price[data-product-id="${this.dataset.productId}"]`);
        const block = incoming?.querySelector('.ts-price-block');
        const current = this.querySelector('.ts-price-block');

        if (block && current) current.replaceWith(block);
      })
      .catch(() => {
        // Variante trocada de novo antes desta resolver: o fetch anterior
        // é abortado de propósito. Manter o preço atual é o correto.
      });
  };
}

if (!customElements.get('ts-pix-price')) {
  customElements.define('ts-pix-price', TsPixPrice);
}

/**
 * Barra de progresso de frete grátis.
 *
 * Tratada como incentivo de upsell, não como promessa: o texto diz
 * quanto falta e só vira confirmação quando a meta é batida.
 */
class TsFreeshipBar extends Component {
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#render(Number(this.dataset.cartTotal) || 0);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#controller?.abort();
  }

  #handleCartUpdate = () => {
    this.#controller?.abort();
    this.#controller = new AbortController();

    fetch(`${this.dataset.cartUrl || '/cart'}.js`, { signal: this.#controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart) this.#render(cart.total_price);
      })
      .catch(() => {
        // Requisição abortada ou rede indisponível: mantém o último
        // estado renderizado em vez de zerar a barra.
      });
  };

  /**
   * @param {number} total - Total do carrinho, em centavos.
   */
  #render(total) {
    const target = Number(this.dataset.target) || 0;
    const msg = this.querySelector('[data-ts-freeship-msg]');
    const fill = this.querySelector('[data-ts-freeship-fill]');
    if (!target || !msg || !fill) return;

    const pct = Math.min(100, Math.round((total / target) * 100));
    fill.style.width = `${pct}%`;

    const bar = this.querySelector('[role="progressbar"]');
    if (bar) bar.setAttribute('aria-valuenow', String(pct));

    if (total >= target) {
      msg.innerHTML = `<strong>${this.dataset.msgReached || 'Você ganhou frete grátis!'}</strong>`;
      return;
    }

    const remaining = formatMoney(target - total, this.dataset.moneyFormat, this.dataset.currency);
    const template = this.dataset.msgRemaining || 'Faltam [valor] para o frete grátis';
    msg.innerHTML = template.replace('[valor]', `<strong>${remaining}</strong>`);
  }
}

if (!customElements.get('ts-freeship-bar')) {
  customElements.define('ts-freeship-bar', TsFreeshipBar);
}

/**
 * Adiciona o look inteiro ao carrinho.
 *
 * Não reimplementa o carrinho: monta o mesmo CartLinesUpdateEvent que
 * o <product-form-component> nativo dispara, com a promise diferida
 * que o cart drawer aguarda. Por isso o drawer abre sozinho e o
 * contador do header atualiza, sem nenhum código de UI aqui.
 */
class TsLookAdd extends Component {
  #button = null;
  #error = null;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#button = this.querySelector('[data-ts-look-add]');
    this.#error = this.querySelector('[data-ts-look-error]');
    this.#button?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#button?.removeEventListener('click', this.#handleClick);
  }

  /** Variantes disponíveis do look. Itens esgotados não têm data-variant-id. */
  #collectItems() {
    const section = this.closest('.shopify-section') ?? document;
    return [...section.querySelectorAll('[data-variant-id]')].map((el) => ({
      id: Number(el.dataset.variantId),
      quantity: 1,
    }));
  }

  #setLabel(key) {
    if (!this.#button) return;
    const label = this.#button.dataset[key];
    if (label) this.#button.textContent = label;
  }

  #showError(message) {
    if (!this.#error) return;
    this.#error.textContent = message;
    this.#error.hidden = false;
  }

  #handleClick = async () => {
    if (this.#busy || !this.#button) return;

    const items = this.#collectItems();
    if (this.#error) this.#error.hidden = true;

    if (!items.length) {
      this.#showError('Nenhum item deste look está disponível no momento.');
      return;
    }

    this.#busy = true;
    this.#button.disabled = true;
    this.#setLabel('labelLoading');

    // Os section ids do carrinho vão junto para o Shopify devolver o
    // HTML já re-renderizado do drawer, igual ao fluxo nativo.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );

    try {
      // fetchConfig('javascript') APAGA o Content-Type — é o que o
      // fluxo nativo quer, porque ele manda FormData. Aqui o corpo é
      // JSON (vários itens de uma vez), então o cabeçalho precisa
      // voltar depois: sem ele o corpo chega como text/plain e o
      // /cart/add.js recusa o pedido inteiro.
      const config = fetchConfig('javascript', {
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'application/json' },
      }).then((r) => r.json());

      if (response.status) {
        deferred.resolve({ cart: null, detail: { didError: true } });
        this.#showError(response.description || response.message || 'Não foi possível adicionar o look.');
        this.#setLabel('labelDefault');
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ts-shop-the-look',
          itemCount: items.length,
          sections: response.sections,
          didError: false,
        },
      });

      this.#setLabel('labelDone');
      setTimeout(() => this.#setLabel('labelDefault'), 2500);
    } catch (error) {
      deferred.resolve({ cart: null, detail: { didError: true } });
      this.#showError('Não foi possível adicionar o look. Tente novamente.');
      this.#setLabel('labelDefault');
    } finally {
      this.#busy = false;
      this.#button.disabled = false;
    }
  };
}

if (!customElements.get('ts-look-add')) {
  customElements.define('ts-look-add', TsLookAdd);
}

/**
 * Liga os marcadores da imagem às linhas da lista, nos dois sentidos.
 * Progressive enhancement: sem JS os marcadores somem do fluxo mas a
 * lista de produtos e os links continuam funcionando.
 */
(() => {
  const link = (section) => {
    const spots = section.querySelectorAll('[data-ts-hotspot]');
    const items = section.querySelectorAll('[data-ts-look-item]');
    if (!spots.length) return;

    const setActive = (index, on) => {
      const spot = section.querySelector(`[data-ts-hotspot="${index}"]`);
      const item = section.querySelector(`[data-ts-look-item="${index}"]`);
      spot?.setAttribute('aria-current', on ? 'true' : 'false');
      item?.setAttribute('aria-current', on ? 'true' : 'false');
    };

    spots.forEach((spot) => {
      const index = spot.dataset.tsHotspot;
      spot.addEventListener('mouseenter', () => setActive(index, true));
      spot.addEventListener('mouseleave', () => setActive(index, false));
      spot.addEventListener('focus', () => setActive(index, true));
      spot.addEventListener('blur', () => setActive(index, false));
      spot.addEventListener('click', () => {
        section
          .querySelector(`[data-ts-look-item="${index}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    items.forEach((item) => {
      const index = item.dataset.tsLookItem;
      item.addEventListener('mouseenter', () => setActive(index, true));
      item.addEventListener('mouseleave', () => setActive(index, false));
    });
  };

  const init = () => document.querySelectorAll('.ts-look').forEach((el) => link(el.closest('.shopify-section') ?? el));

  // Como módulo, este arquivo roda com readyState 'interactive' e o
  // DOMContentLoaded ainda por vir: registrar os dois ligava os
  // listeners em dobro.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // O editor de temas recarrega a seção sem recarregar a página.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) link(event.target);
  });
})();

/**
 * <ts-shipping-calc> — cotação de frete na página do produto.
 *
 * A Shopify não cota frete de um produto isolado: a API cota o
 * carrinho. O caminho é adicionar a variante, pedir as taxas e
 * desfazer a adição, devolvendo a linha à quantidade que tinha.
 *
 * Duas regras que este componente respeita:
 *   1. NUNCA `/cart/clear.js` — apagaria o carrinho do cliente.
 *   2. As chamadas não disparam os eventos de carrinho do tema, para
 *      o drawer não abrir nem o contador piscar durante a consulta.
 *
 * Se a variante não puder ser adicionada (esgotada), a cotação ainda
 * acontece com o carrinho como está — melhor um valor aproximado do
 * que uma caixa morta.
 */
class TsShippingCalc extends Component {
  #form = null;
  #input = null;
  #button = null;
  #panel = null;
  #results = null;
  #message = null;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.querySelector('[data-ts-ship-form]');
    this.#input = this.querySelector('[data-ts-ship-input]');
    this.#button = this.querySelector('[data-ts-ship-btn]');
    this.#panel = this.querySelector('[data-ts-ship-panel]');
    this.#results = this.querySelector('[data-ts-ship-results]');
    this.#message = this.querySelector('[data-ts-ship-msg]');

    this.#form?.addEventListener('submit', this.#handleSubmit);
    this.#input?.addEventListener('input', this.#handleInput);
    this.#results?.addEventListener('click', this.#handleResultsClick);

    // localStorage pode lançar em navegador de app (Instagram) ou com
    // armazenamento bloqueado: o CEP salvo é só conveniência.
    let saved = null;
    try {
      saved = localStorage.getItem('ts-cep');
    } catch (_) {}
    if (saved && this.#input) this.#input.value = saved;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('submit', this.#handleSubmit);
    this.#input?.removeEventListener('input', this.#handleInput);
    this.#results?.removeEventListener('click', this.#handleResultsClick);
  }

  /** Máscara 00000-000 conforme digita, sem bloquear apagar. */
  #handleInput = () => {
    if (!this.#input) return;
    const digits = this.#input.value.replace(/\D/g, '').slice(0, 8);
    this.#input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  /** "Alterar CEP" devolve o formulário e limpa o resultado. */
  #handleResultsClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-ts-ship-change]')) return;
    this.#togglePanel(true);
    if (this.#results) this.#results.hidden = true;
    this.#input?.focus();
    this.#input?.select();
  };

  #togglePanel(show) {
    if (this.#panel) this.#panel.hidden = !show;
  }

  #show(text, isError = false) {
    if (!this.#message) return;
    this.#message.textContent = text;
    this.#message.classList.toggle('ts-ship__msg--error', isError);
    this.#message.hidden = !text;
  }

  #skeleton() {
    if (!this.#results) return;
    this.#results.innerHTML = '<div class="ts-ship__skeleton"><span></span><span></span></div>';
    this.#results.hidden = false;
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy || !this.#input) return;

    const zip = this.#input.value.replace(/\D/g, '');
    if (zip.length !== 8) {
      if (this.#results) this.#results.hidden = true;
      this.#show(this.dataset.msgInvalid, true);
      return;
    }

    this.#busy = true;
    if (this.#button) {
      this.#button.disabled = true;
      this.#button.textContent = this.dataset.labelLoading;
    }
    this.#show('');
    this.#skeleton();

    try {
      const place = await TsShippingCalc.locate(zip);
      if (!place?.province) {
        if (this.#results) this.#results.hidden = true;
        this.#show(this.dataset.msgInvalid, true);
        return;
      }

      try {
        localStorage.setItem('ts-cep', this.#input.value);
      } catch (_) {}

      const rates = await this.#quote(zip, place);
      this.#render(rates, zip, place);
    } catch (error) {
      if (this.#results) this.#results.hidden = true;
      this.#show(this.dataset.msgError, true);
    } finally {
      this.#busy = false;
      if (this.#button) {
        this.#button.disabled = false;
        this.#button.textContent = this.dataset.labelIdle;
      }
    }
  };

  /**
   * Cota o frete num carrinho À PARTE, criado pela Storefront API da
   * própria loja — o carrinho da cliente nem é tocado.
   *
   * Antes a peça era somada ao carrinho real via /cart/add.js e depois
   * desfeita: cada cotação disparava 4–5 eventos product_added_to_cart
   * (Meta, GA e funil da Shopify contavam "adicionou ao carrinho" falso).
   * Pela Storefront API: 0 eventos (medido em 07/10/2026).
   *
   * As tarifas de transportadora (Frenet) só vêm com @defer. O prazo
   * (`deliveryPromise`) só existe na versão `unstable` da API; se ela
   * mudar ou falhar, a cotação repete na versão estável, só sem prazo.
   *
   * A variante, a quantidade e as propriedades saem do formulário nativo
   * do produto. O carrinho de cotação tem só esta peça, então o frete
   * grátis por valor considera a peça, não o carrinho inteiro.
   */
  async #quote(zip, place) {
    const scope = this.closest('.shopify-section') ?? document;
    const form = scope.querySelector('form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) throw new Error('form');

    const data = new FormData(form);
    const variantId = data.get('id');
    if (!variantId) throw new Error('variant');

    const attributes = [];
    data.forEach((value, key) => {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string' && value) attributes.push({ key: match[1], value });
    });

    const input = {
      lines: [
        {
          merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
          quantity: Number(data.get('quantity')) || 1,
          attributes,
        },
      ],
      buyerIdentity: { countryCode: this.dataset.country || 'BR' },
      delivery: {
        addresses: [
          {
            selected: true,
            address: {
              deliveryAddress: {
                zip: TsShippingCalc.maskCep(zip),
                countryCode: this.dataset.country || 'BR',
                provinceCode: place.province,
                city: place.city || '',
                address1: '-',
              },
            },
          },
        ],
      },
    };

    try {
      return await TsShippingCalc.storefrontRates('unstable', input, true);
    } catch {
      return TsShippingCalc.storefrontRates('2025-07', input, false);
    }
  }

  /**
   * @param {string} version
   * @param {object} input - CartInput
   * @param {boolean} withPromise - pede o prazo (só existe em `unstable`)
   * @returns {Promise<Array<{name: string, price: string, promise: string}>>}
   */
  static async storefrontRates(version, input, withPromise) {
    const query = `mutation($i: CartInput!) {
      cartCreate(input: $i) {
        cart { id ... @defer { deliveryGroups(first: 5, withCarrierRates: true) {
          nodes { deliveryOptions { title deliveryMethodType estimatedCost { amount }${withPromise ? ' deliveryPromise' : ''} } }
        } } }
        userErrors { message }
      }
    }`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);

    const response = await fetch(`/api/${version}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'multipart/mixed' },
      body: JSON.stringify({ query, variables: { i: input } }),
      signal: controller.signal,
    }).finally(() => clearTimeout(timeout));

    if (!response.ok) throw new Error('storefront');

    // Resposta multipart (@defer): cada parte é um JSON. As tarifas vêm
    // na parte "incremental".
    const text = await response.text();
    const parts = text
      .split(String.fromCharCode(10))
      .map((line) => line.trim())
      .filter((line) => line.startsWith('{'))
      .map((line) => {
        try {
          return JSON.parse(line);
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    if (parts.some((part) => part.errors?.length)) throw new Error('graphql');

    const groups = parts
      .flatMap((part) => part.incremental ?? [])
      .map((item) => {
        if (item.errors?.length) throw new Error('graphql');
        return item.data?.deliveryGroups?.nodes ?? [];
      })
      .flat();

    return groups
      .flatMap((group) => group.deliveryOptions ?? [])
      .filter((option) => option.deliveryMethodType !== 'PICK_UP')
      .map((option) => ({
        name: option.title ?? '',
        price: option.estimatedCost?.amount ?? '0',
        promise: option.deliveryPromise ?? '',
      }));
  }

  #render(rates, zip, place) {
    if (!this.#results) return;

    if (!rates.length) {
      this.#results.hidden = true;
      this.#show(this.dataset.msgEmpty, false);
      return;
    }

    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    const local = place.city ? `${place.city}/${place.province}` : place.province;

    const cards = rates
      .map((rate) => {
        const cents = Math.round(parseFloat(rate.price) * 100);
        const price = cents > 0 ? formatMoney(cents, format, currency) : this.dataset.freeLabel;
        const eta = TsShippingCalc.eta(rate);

        return `
          <div class="ts-ship__rate${cents > 0 ? '' : ' ts-ship__rate--free'}">
            <span class="ts-ship__rate-name">
              ${TsShippingCalc.escape(rate.name)}
              ${eta ? `<span class="ts-ship__rate-eta">${eta}</span>` : ''}
            </span>
            <span class="ts-ship__rate-price">${price}</span>
          </div>
        `;
      })
      .join('');

    this.#results.innerHTML = `
      <div class="ts-ship__summary">
        <span>Entregas para <strong>${TsShippingCalc.maskCep(zip)}</strong>${local ? ` · ${local}` : ''}</span>
        <button type="button" class="ts-ship__change" data-ts-ship-change>${this.dataset.labelChange}</button>
      </div>
      <div class="ts-ship__list">${cards}</div>
    `;

    this.#results.hidden = false;
    this.#togglePanel(false);
    this.#show('');
  }

  static maskCep(zip) {
    return zip.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  }

  /**
   * Estado (e cidade) do CEP. Tenta o ViaCEP, que ainda devolve a
   * cidade para o resumo; se falhar ou demorar, a faixa de CEP resolve
   * a UF sozinha — a cotação não pode depender de um serviço de
   * terceiro estar de pé.
   */
  /** @param {string} text */
  static escape(text) {
    return String(text ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  static async locate(zip) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3500);

      const data = await fetch(`https://viacep.com.br/ws/${zip}/json/`, { signal: controller.signal })
        .then((r) => (r.ok ? r.json() : null))
        .finally(() => clearTimeout(timeout));

      if (data && !data.erro && data.uf) {
        return { province: data.uf, city: data.localidade || '' };
      }
    } catch {
      /* cai para a tabela local */
    }

    const uf = TsShippingCalc.ufFromCep(zip);
    return uf ? { province: uf, city: '' } : null;
  }

  /**
   * Prazo do frete. A loja responde em `delivery_days` ([min, max]);
   * `delivery_range` (datas) só aparece em algumas transportadoras,
   * então fica como segunda opção.
   */
  static eta(rate) {
    // Storefront API (unstable): a loja já devolve o texto, ex. "6 dias úteis".
    if (rate.promise) return `Chega em ${rate.promise}`;

    const days = rate.delivery_days;
    if (Array.isArray(days) && days.length) {
      const min = Number(days[0]);
      const max = Number(days[days.length - 1]);
      if (Number.isFinite(min) && Number.isFinite(max)) {
        if (min === max) return max === 1 ? 'Chega em 1 dia útil' : `Chega em ${max} dias úteis`;
        return `Chega em ${min} a ${max} dias úteis`;
      }
    }

    const range = rate.delivery_range;
    if (!Array.isArray(range) || range.length === 0) return '';

    const today = new Date();
    const toDays = (value) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return Math.max(1, Math.ceil((date - today) / 86400000));
    };

    const start = toDays(range[0]);
    const end = toDays(range[range.length - 1]);
    if (start == null && end == null) return '';
    if (start && end && start !== end) return `Chega em ${start} a ${end} dias`;
    return `Chega em até ${end ?? start} dias`;
  }

  /**
   * UF a partir da faixa de CEP (tabela dos Correios). É o plano B do
   * ViaCEP e roda sem nenhuma requisição.
   */
  static ufFromCep(zip) {
    const n = Number(zip);
    if (!Number.isFinite(n)) return null;

    const ranges = [
      ['SP', 1000000, 19999999],
      ['RJ', 20000000, 28999999],
      ['ES', 29000000, 29999999],
      ['MG', 30000000, 39999999],
      ['BA', 40000000, 48999999],
      ['SE', 49000000, 49999999],
      ['PE', 50000000, 56999999],
      ['AL', 57000000, 57999999],
      ['PB', 58000000, 58999999],
      ['RN', 59000000, 59999999],
      ['CE', 60000000, 63999999],
      ['PI', 64000000, 64999999],
      ['MA', 65000000, 65999999],
      ['PA', 66000000, 68899999],
      ['AP', 68900000, 68999999],
      ['AM', 69000000, 69299999],
      ['RR', 69300000, 69389999],
      ['AM', 69400000, 69899999],
      ['AC', 69900000, 69999999],
      ['DF', 70000000, 72799999],
      ['GO', 72800000, 72999999],
      ['DF', 73000000, 73699999],
      ['GO', 73700000, 76799999],
      ['RO', 76800000, 76999999],
      ['TO', 77000000, 77999999],
      ['MT', 78000000, 78899999],
      ['RO', 78900000, 78999999],
      ['MS', 79000000, 79999999],
      ['PR', 80000000, 87999999],
      ['SC', 88000000, 89999999],
      ['RS', 90000000, 99999999],
    ];

    const found = ranges.find(([, from, to]) => n >= from && n <= to);
    return found ? found[0] : null;
  }
}

if (!customElements.get('ts-shipping-calc')) {
  customElements.define('ts-shipping-calc', TsShippingCalc);
}

/**
 * Modelo da calcinha (Tanga, Fita, Asa delta, Basic).
 *
 * A escolha viaja como propriedade de linha, não como variante — o
 * porquê está no comentário de blocks/ts-bottom-style.liquid.
 *
 * O <product-form-component> nativo monta o corpo do add-to-cart com
 * `new FormData(form)`, então basta existir um input dentro do form.
 * Este bloco fica fora dele (o form pertence ao buy-buttons), então
 * aqui o input é injetado em cada form de add-to-cart da página,
 * inclusive o do sticky add-to-cart.
 *
 * A injeção é refeita no submit, e não só uma vez ao carregar, porque
 * a troca de variante re-renderiza a seção e levaria o input embora
 * sem aviso — o pedido chegaria sem a modelagem escolhida.
 */
class TsBottomStyle extends Component {
  #inputs = [];

  connectedCallback() {
    super.connectedCallback();

    this.#inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
    this.addEventListener('change', this.#handleChange);

    // Captura no WINDOW: o component.js do tema delega os on:submit num
    // listener de captura no document, registrado antes deste. No mesmo
    // alvo e fase vence quem registrou primeiro, então no document o
    // pedido já saía sem o modelo. A captura do window roda antes.
    window.addEventListener('submit', this.#handleSubmit, true);
    // "Compre já" (botão de checkout dinâmico da Shopify) não dispara
    // submit: vai direto ao checkout. Barrado no clique, também no window.
    window.addEventListener('click', this.#handleBuyNow, true);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
    window.removeEventListener('submit', this.#handleSubmit, true);
    window.removeEventListener('click', this.#handleBuyNow, true);
  }

  /** @returns {string} Modelo marcado, ou vazio. */
  get value() {
    return this.#inputs.find((input) => input.checked)?.value ?? '';
  }

  /** @returns {string} Nome da propriedade como ela aparece no pedido. */
  get propertyName() {
    return this.dataset.property || 'Modelo da calcinha';
  }

  #handleChange = () => {
    // O mesmo par de atributos que o seletor de variante usa: é ele
    // que faz a pílula deslizar de um botão para o outro no CSS.
    for (const input of this.#inputs) {
      input.dataset.previousChecked = input.dataset.currentChecked;
      input.dataset.currentChecked = String(input.checked);
    }

    const chosen = this.querySelector('[data-ts-bottom-chosen]');
    if (chosen) chosen.textContent = this.value;

    this.#hideError();
    this.#sync();
  };

  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;
    if (!this.#ownsForm(form)) return;

    if (this.dataset.required === 'true' && !this.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showError();
      return;
    }

    this.#sync(form);
  };

  #handleBuyNow = (event) => {
    if (this.dataset.required !== 'true' || this.value) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const buyNow = target.closest('shopify-buy-it-now-button, shopify-accelerated-checkout, .shopify-payment-button');
    if (!buyNow) return;
    const form = buyNow.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement) || !this.#ownsForm(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError();
  };

  /**
   * Garante o input escondido, com o valor atual, dentro dos forms de
   * add-to-cart.
   *
   * @param {HTMLFormElement} [only] - Um form específico; sem ele, todos.
   */
  #sync(only) {
    const candidates = only ? [only] : document.querySelectorAll('form[data-type="add-to-cart-form"]');
    const forms = Array.from(candidates).filter((form) => this.#ownsForm(form));
    const name = `properties[${this.propertyName}]`;
    const value = this.value;

    for (const form of forms) {
      let input = form.querySelector(`input[data-ts-bottom-input="${this.id || 'ts-bottom'}"]`);

      if (!value) {
        // Sem escolha não se manda propriedade vazia: ela apareceria
        // em branco no pedido como se fosse um dado real.
        input?.remove();
        continue;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.dataset.tsBottomInput = this.id || 'ts-bottom';
        form.appendChild(input);
      }

      input.name = name;
      input.value = value;
    }
  }

  /**
   * A PDP também tem add-to-cart de outros produtos ("Combina com",
   * recomendações). Sem esta checagem, a exigência de escolher a
   * modelagem travaria a compra deles — e a propriedade entraria no
   * pedido errado.
   *
   * @param {HTMLFormElement} form
   * @returns {boolean} Se o form é do produto desta página.
   */
  #ownsForm(form) {
    const owner = form.closest('product-form-component');
    if (!(owner instanceof HTMLElement)) return false;
    return owner.dataset.productId === this.dataset.productId;
  }

  #showError() {
    const error = this.querySelector('[data-ts-bottom-error]');
    if (!error) return;

    error.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.#inputs[0]?.focus({ preventScroll: true });
  }

  #hideError() {
    const error = this.querySelector('[data-ts-bottom-error]');
    if (error) error.hidden = true;
  }
}

if (!customElements.get('ts-bottom-style')) {
  customElements.define('ts-bottom-style', TsBottomStyle);
}

/**
 * Carrossel sob demanda (sections/product-list → setting ts_lazy_after).
 *
 * A página traz só o 1º lote de peças; o Liquid deixa um marcador
 * [data-ts-lazy-list] com a página atual e o total. Quando o último slide
 * chega perto da área visível do carrossel, o próximo lote vem do servidor
 * pela Section Rendering API (?section_id=…&page=N) e os slides entram no
 * fim do trilho — o mesmo comportamento do carrossel da loja antiga. O
 * slideshow nativo atualiza seus refs sozinho (MutationObserver do
 * Component); aqui só se reabilita a seta "próximo", que pode ter sido
 * desligada ao chegar no fim do lote anterior.
 *
 * O observador usa o próprio trilho como raiz: um slide fora do trilho
 * nunca "entra" na tela da página.
 */
(() => {
  const setup = (root) => {
    root.querySelectorAll('[data-ts-lazy-list]').forEach((marker) => {
      if (!(marker instanceof HTMLElement) || marker.dataset.tsReady) return;
      marker.dataset.tsReady = 'true';

      const section = marker.closest('.shopify-section');
      const scroller = section?.querySelector('slideshow-slides');
      const sectionId = marker.dataset.sectionId;
      let page = Number(marker.dataset.page) || 1;
      const pages = Number(marker.dataset.pages) || 1;
      if (!section || !scroller || !sectionId || page >= pages || !('IntersectionObserver' in window)) return;

      let busy = false;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) load();
        },
        { root: scroller, rootMargin: '0px 100% 0px 100%' }
      );

      const watchLast = () => {
        observer.disconnect();
        const last = scroller.lastElementChild;
        if (last && page < pages) observer.observe(last);
      };

      const load = async () => {
        if (busy || page >= pages) return;
        busy = true;
        try {
          const url = new URL(window.location.pathname, window.location.origin);
          url.searchParams.set('section_id', sectionId);
          url.searchParams.set('page', String(page + 1));
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('lote');

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const slides = Array.from(doc.querySelector('slideshow-slides')?.children ?? []).filter(
            (element) => element.tagName === 'SLIDESHOW-SLIDE'
          );

          for (const slide of slides) {
            slide.setAttribute('aria-hidden', 'true');
            scroller.append(document.importNode(slide, true));
          }

          page = slides.length ? page + 1 : pages;
          const next = section.querySelector('.slideshow-control--next');
          if (next instanceof HTMLButtonElement && slides.length) next.disabled = false;
        } catch {
          // Falhou o lote: para de tentar. O carrossel segue com o que já tem.
          page = pages;
        } finally {
          busy = false;
          watchLast();
        }
      };

      watchLast();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(document));
  else setup(document);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) setup(event.target);
  });
})();
